import wixData from 'wix-data'
import wixWindow from 'wix-window'
import { session } from 'wix-storage'
import wixLocationFrontend from 'wix-location-frontend'
import wixSeoFrontend from 'wix-seo-frontend'

import { getAuthorSobdomains, getDictionary } from '@wix-platform/blog-cross-posts-backend'
import { POSTS_COLLECTION_ID, AUTHOR_URL_PATH } from '../public/settings'

const POSTS_PER_PAGE = 3

let dictionary = {}

export async function init() {
    const [_dictionary, author] = await Promise.all([
        getDictionary(),
        getAuthor()
    ])
    dictionary = _dictionary

    setPageTitle(author)
    await Promise.all([
        renderAuthorInfo(author),
        handlePosts(author._id),
        setAuothorStructuredData(author),
        setAuthorCanonical(author.canonical)
    ])
    backButton()
}

const setPageTitle = (author) => {
    const { name, title } = author
    $w('#pagination').customPageTitleTemplate = `${name}, ${title}{page}`
}

const getAuthor = async () => {
    const $dataSet = $w('#dynamicDataset')
    await $dataSet.onReadyAsync()
    return $dataSet.getCurrentItem()
}

const renderAuthorInfo = async (author) => {
    const {
        name,
        description,
        profilePhoto,
        website,
        subDomains,
        linkedinLink,
        twitterLink,
        facebookLink,
        instagramLink,
        tiktokLink,
        youtubeLink
    } = author

    $w('#name').text = name
    $w('#description').text = description
    $w('#profilePhoto').src = profilePhoto
    $w('#profilePhoto').alt = name

    if (website) {
        $w('#website').text = website
        $w('#website').expand()
    }

    const socialLinks = [linkedinLink, twitterLink, facebookLink, instagramLink, tiktokLink, youtubeLink]

    $w('#socialBar').forEachItem(($item, itemData, index) => {
        const socialLink = socialLinks[index]
        if (socialLink) {
            $item('#socialContainer').expand()
            $item('#socialButton').link = socialLink
        } else {
            $item('#socialContainer').collapse()
        }
    })

    await otherBlogsList(author, subDomains)
}

const otherBlogsList = async (author, subDomainKeys) => {
    if (!subDomainKeys) return

    const $otherBlogsList = $w('#otherBlogsList')

    const subDomains = await getAuthorSobdomains(subDomainKeys)
    let subDomainsText = ''
    subDomains.forEach((subDomain, index) => {
        const { name, url } = subDomain

        const isLast = (subDomains.length === index + 1)
        const isFirst = (index === 0)
        if (!isLast && !isFirst) subDomainsText += ', '
        if (isLast && !isFirst) subDomainsText += dictionary[' and ']

        const authorLink = `${url}/${AUTHOR_URL_PATH}/${author._id}`
        subDomainsText += `<a style="text-decoration:underline" href=${authorLink}>${name}</a>`
    })

    const variables = {
        authorName: author.name,
        blogsList: subDomainsText
    }
    updateTemplatedElement($otherBlogsList, 'html', variables)

    $otherBlogsList.expand()
}

const handlePosts = async (authorId) => {
    const $pagination = $w('#pagination')

    $w('#posts').onItemReady(renderPost)
    const currentPage = $pagination.getCurrentPage()
    const postsResults = await getPosts(authorId, currentPage)
    $pagination.totalPages = postsResults.totalPages
    updatePosts(postsResults)

    $pagination.onChange(async event => {
        const { page } = event.data
        const postsResults = await getPosts(authorId, page)
        updatePosts(postsResults)
    })
}

const getPosts = (authorId, page) => {
    const skip = (page - 1) * POSTS_PER_PAGE

    return wixData.query(POSTS_COLLECTION_ID)
        .hasSome('authors', authorId)
        .include('blog')
        .limit(POSTS_PER_PAGE)
        .skip(skip)
        .find()
}

const renderPost = ($item, post) => {
    const { image, title, url, blog } = post

    const $postImage = $item('#postImage')
    const $postCategory = $item('#postCategory')
    const $postTitle = $item('#postTitle')

    $postImage.src = image
    $postImage.alt = title
    $postImage.link = url
    $postImage.target = '_self'
    $postCategory.label = blog.name.toUpperCase()
    $postCategory.target = '_self'
    $postCategory.link = blog.url

    linkTextElement($postTitle, title, url)
    handleFrenchSpace($postTitle)
}

const updatePosts = (postsResults) => {
    $w('#posts').data = postsResults.items
    updateTemplatedElement($w('#postsCount'), 'text', {
        count: postsResults.totalCount
    })
}

const linkTextElement = ($element, text, link, newTab = false) => {
    $element.text = text
    const target = (newTab) ? '_blank' : '_self'
    const linkedText = `<a href="${link}" target="${target}">${text}</a>`
    $element.html = $element.html.replace(text, linkedText)
}

// In French all forms of "high" punctuation have a space preceding them,
// we replace that space with a &nbsp; so it won't cause an ugly line wrap.
const handleFrenchSpace = ($element) => {
    const text = $element.html.replace(/(<([^>]+)>)/g, '') // strip HTML to get only the text
    const textWithFrenchSpaceFix = text.replace(/( )(?=[^ ]$)/, '&nbsp;')
    $element.html = $element.html.replace(text, textWithFrenchSpaceFix)
}

const updateTemplatedElement = ($element, parameter, variables) => {
    Object.keys(variables).forEach((key) => {
        $element[parameter] = $element[parameter].replaceAll(`{{${key}}}`, variables[key])
    })
}

const backButton = () => {
    if (wixWindow.rendering.env !== 'browser') return

    const previousPage = session.getItem('previousPage')
    const $backButton = $w('#backButton')
    $backButton.link = previousPage || wixWindow.referrer || wixLocationFrontend.baseUrl
    $backButton.target = '_self'
    $backButton.enable()
}

function setAuothorStructuredData(author) {
    const {
        name,
        title,
        profilePhoto,
        website,
        linkedinLink,
        twitterLink,
        facebookLink,
        instagramLink,
        tiktokLink,
        youtubeLink
    } = author

    const { baseUrl, prefix, path } = wixLocationFrontend
    const url = `${baseUrl}/${prefix}/${path}`

    const imageData = (profilePhoto) ? getImageData(profilePhoto) : {}

    const sameAs = [
        website,
        linkedinLink,
        twitterLink,
        facebookLink,
        instagramLink,
        tiktokLink,
        youtubeLink
    ].filter((link) => link)

    const blogPostStructuredData = {
        "@context": "https://schema.org/",
        "@type": "Person",
        "name": name,
        "url": url,
        "image": {
            "@type": "ImageObject",
            "url": imageData.url,
            "width": imageData.width,
            "height": imageData.height
        },
        "sameAs": sameAs,
        "jobTitle": title,
        "worksFor": {
            "@type": "Organization",
            "name": "Wix.com"
        }
    }

    return wixSeoFrontend.setStructuredData([blogPostStructuredData])
}

const setAuthorCanonical = (canonical) => {
    console.log('links', wixSeoFrontend.links)

    if (!canonical || !isValidHttpUrl(canonical)) return

    return wixSeoFrontend
        .setLinks([{
                rel: 'canonical',
                href: canonical
            }
        ])
}

const isValidHttpUrl = (string) => {
  try {
    const newUrl = new URL(string);
    return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
  } catch (err) {
    return false;
  }
}

const getImageData = (image) => {
    const match = image.match(/(?:wix:image:\/\/v\d+\/)([a-zA-Z0-9_~]+\.jpg).*#originWidth=(\d+)&originHeight=(\d+)/)
    if (!match) return { url: image }
    const [, fileName, width, height] = match
    const url = `https://static.wixstatic.com/media/${fileName}`
    return { fileName, url, width, height }
}